import { useEnvironments } from "@app-context/environments/context";
import { APISource, APISources } from "@app-context/networkCalls/types";
import { getSelectOption } from "@enfusion-ui/utils";
import {
  IconButton,
  SecondaryTabList,
  Select,
  TabListTab,
  VisibleIf,
} from "@enfusion-ui/web-components";
import { styled } from "@enfusion-ui/web-core";
import { faCode } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import { AuthView } from "view/AuthView";

import {
  InspectorHTTPConfig,
  InspectorOEMSConfig,
  InspectorReportConfig,
  InspectorRestConfig,
  RequestType,
  RequestTypes,
  useInspectorContext,
} from "../context/inspector/context";
import { InspectorConfig } from "./config/InspectorConfig";
import { InspectorContent } from "./content/InspectorContent";

const ToggleButton = styled.div`
  position: absolute;
  right: 0px;
  top: 6px;
  justify-content: flex-end;
  display: flex;
  padding-right: var(--spacing);
`;

const HeaderTab = styled.div`
  max-height: 234px;
  position: relative;
`;

const getHeaderTabs = (
  type: RequestType,
  config:
    | InspectorHTTPConfig
    | InspectorRestConfig
    | InspectorReportConfig
    | InspectorOEMSConfig,
  apiSource: APISource
) => {
  const tabs: TabListTab[] = [];
  switch (type) {
    case RequestTypes.HTTP:
      tabs.push(
        ...[
          {
            label: "Parameters",
            key: "params",
          },
          {
            label: "Headers",
            key: "headers",
          },
        ]
      );
      if (
        ["POST", "PUT", "PATCH"].includes(
          (config as InspectorHTTPConfig).method
        )
      ) {
        tabs.push({ label: "Body", key: "body" });
      }
      break;
    case RequestTypes.REPORT:
      if (apiSource === APISources.CORE)
        tabs.push({
          label: "Authentication",
          key: "auth-token",
        });
      break;
    case RequestTypes.ENFUSION:
      tabs.push(
        ...[
          {
            label: "Parameters",
            key: "params",
          },
          {
            label: "Headers",
            key: "headers",
          },
          {
            label: "Pre Filter",
            key: "pre-filter",
          },
        ]
      );
      if (
        ["POST", "PUT", "PATCH"].includes(
          (config as InspectorRestConfig).method
        )
      ) {
        tabs.push({ label: "Body", key: "body" });
      }
      if (apiSource === APISources.CORE)
        tabs.splice(0, 0, {
          label: "Authentication",
          key: "auth-token",
        });
      break;
    case RequestTypes.OEMS:
      if (apiSource === APISources.CORE)
        tabs.push({
          label: "Authentication",
          key: "auth-token",
        });
  }
  return tabs;
};

export const InspectorContentWrapper = styled.div`
  flex: 1;
  margin-top: var(--spacing);
  border: 1px solid var(--primary);
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ViewContainer = styled.div`
  height: 100%;
  padding: var(--spacing);
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TopRow = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  height: auto;
  overflow: auto;
`;

export const MethodsWrapper = styled.div`
  width: 200px;
  margin-right: var(--spacing);
`;

const VIEW_API_OPTIONS = Object.entries(APISources).map((i) => ({
  label: i[0],
  value: i[1],
}));
const VIEW_TYPE_OPTIONS = [
  { label: "HTTP", value: RequestTypes.HTTP },
  { label: "ENFUSION", value: RequestTypes.ENFUSION },
  { label: "REPORT STREAM", value: RequestTypes.REPORT },
  { label: "OEMS STREAM", value: RequestTypes.OEMS },
];

export const InspectorView: React.FC<{}> = () => {
  const {
    handleTypeChange,
    requestType,
    config,
    apiSource,
    environment,
    handleAPISourceChange,
    handleEnvironmentChange,
  } = useInspectorContext();
  const { environments } = useEnvironments();
  const envOptions = React.useMemo(
    () =>
      environments.map((i) => ({ label: i.label, value: JSON.stringify(i) })),
    [JSON.stringify(environments)]
  );

  const [selectedParamTab, setSelectedParamTab] =
    React.useState<string>("auth-token");
  const [paramsOpen, setParamsOpen] = React.useState(true);
  const toggleParamsOpen = () => setParamsOpen((i) => !i);

  React.useEffect(() => {
    if (requestType === RequestTypes.HTTP || apiSource !== APISources.CORE)
      setSelectedParamTab("params");
    else setSelectedParamTab("auth-token");
  }, [requestType, apiSource]);

  return (
    <ViewContainer>
      <TopRow>
        <MethodsWrapper>
          <Select
            options={VIEW_TYPE_OPTIONS}
            value={getSelectOption(VIEW_TYPE_OPTIONS, requestType)}
            clearable={false}
            onChange={(v) => handleTypeChange(v?.value ?? null)}
            label="Type"
          />
        </MethodsWrapper>
        {requestType !== RequestTypes.HTTP && (
          <>
            <MethodsWrapper>
              <Select
                options={VIEW_API_OPTIONS}
                value={getSelectOption(VIEW_API_OPTIONS, apiSource)}
                clearable={false}
                onChange={(v) => handleAPISourceChange(v?.value ?? null)}
                label="API Source"
                minWidth={150}
              />
            </MethodsWrapper>
            <MethodsWrapper>
              <Select
                value={getSelectOption(envOptions, JSON.stringify(environment))}
                options={envOptions}
                onChange={(v) =>
                  handleEnvironmentChange(JSON.parse(v?.value ?? "null"))
                }
                creatable
                clearable={false}
                label="Environment"
                minWidth={150}
              />
            </MethodsWrapper>
          </>
        )}
        <InspectorConfig />
      </TopRow>
      <InspectorContentWrapper>
        {getHeaderTabs(requestType, config, apiSource).length ? (
          <HeaderTab>
            <SecondaryTabList
              value={selectedParamTab}
              onSelect={(key) => {
                setParamsOpen(true);
                setSelectedParamTab(key);
              }}
              tabs={getHeaderTabs(requestType, config, apiSource)}
            />
            <ToggleButton>
              <IconButton
                color={paramsOpen ? "var(--text-normal)" : "var(--primary)"}
                title="Toggle Params"
                icon={faCode}
                size="sm"
                hoverable
                onClick={toggleParamsOpen}
              />
            </ToggleButton>
          </HeaderTab>
        ) : null}
        <>
          <VisibleIf
            visibleStyle={{
              padding: "var(--spacing-xl)",
              height: 200,
              overflow: "auto",
            }}
            visible={paramsOpen && selectedParamTab === "auth-token"}
          >
            <AuthView />
          </VisibleIf>
          <InspectorContent
            selectedParamTab={selectedParamTab}
            paramsOpen={paramsOpen}
            setParamsOpen={setParamsOpen}
          />
        </>
      </InspectorContentWrapper>
    </ViewContainer>
  );
};
